/**
 * Gameplan editor panel — the third side-panel mode alongside the
 * detail Drawer and the List view sheet. Rendered when
 * store.gameplanViewOpen is true.
 *
 * This file is the shell only. Body content is routed based on
 * store.activeGameplanId:
 *   null     → GameplanIndex   (list of saved plans, create/rename/delete)
 *   non-null → GameplanEditor  (title + description + lines for that plan)
 *
 * Shell-level scaffolding lives here — sticky header, ViewSwitcher,
 * ResizeHandle, Esc-to-close, panel ref the resize handle mutates —
 * and stays stable across future phases (Phase 4 line carousel,
 * Phase 5 receptor picker, etc. land inside GameplanEditor without
 * touching this file).
 */
import { useEffect, useRef } from 'react';
import { isSelectionValid, useAppStore } from '../state/store';
import type {
  Counter,
  MediaRefsIndex,
  Move,
  Position,
} from '../types/domain';
import { ViewSwitcher } from './Drawer';
import { GameplanEditor } from './GameplanEditor';
import { GameplanIndex } from './GameplanIndex';
import { ResizeHandle } from './ResizeHandle';
import { useResponsivePanelLayout } from './useResponsivePanelLayout';

// The panel needs the unfiltered entity arrays so the line renderer
// can resolve nodeRefs to display labels regardless of the canvas's
// current filter state. A user might be building a no-gi gameplan
// while looking at a gi-filtered canvas — the editor shouldn't
// suddenly stop being able to label its own nodes. Media refs flow
// through for the cheatsheet export.
type Props = {
  positions: Position[];
  moves: Move[];
  counters: Counter[];
  mediaRefs: MediaRefsIndex | null;
};

export function GameplanPanel({
  positions,
  moves,
  counters,
  mediaRefs,
}: Props) {
  const drawerWidth = useAppStore((s) => s.drawerWidth);
  const selection = useAppStore((s) => s.selection);
  const gameplans = useAppStore((s) => s.gameplans);
  const activeGameplanId = useAppStore((s) => s.activeGameplanId);
  const setActiveGameplan = useAppStore((s) => s.setActiveGameplan);

  // Resize-handle mutates this directly during drag; same pattern
  // ListView uses.
  const panelRef = useRef<HTMLElement>(null);
  // Inner surface ref. In side mode the inner is `position: absolute;
  // right: 0; width: <openWidth>` — its width is set explicitly and
  // does NOT inherit from the aside. During a drag we mutate the
  // aside's width directly, so without also mutating the inner the
  // content would lag behind the boundary. Bottom-sheet mode uses
  // top:0; bottom:0 on the inner so it auto-fills — only the side-
  // mode width case needs this.
  const innerRef = useRef<HTMLDivElement>(null);

  // Esc behavior is two-layered:
  //   - Inside the editor (a plan is active): pop back to the index.
  //   - Otherwise: collapse the panel entirely.
  // When the user is mid-edit in an input/textarea, those components'
  // own keydown handlers cancel the edit; we explicitly skip Esc here
  // so the panel doesn't close out from under them.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const tgt = e.target as HTMLElement | null;
      const tag = tgt?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      e.preventDefault();
      const state = useAppStore.getState();
      if (state.activeGameplanId !== null) {
        state.setActiveGameplan(null);
      } else {
        state.setGameplanViewOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Look up the active gameplan by id. If the id no longer matches a
  // plan (e.g. the user deleted it while it was active), clear the
  // pointer and fall back to the index.
  const activeGameplan =
    activeGameplanId !== null
      ? gameplans.find((g) => g.id === activeGameplanId) ?? null
      : null;
  useEffect(() => {
    if (activeGameplanId !== null && activeGameplan === null) {
      setActiveGameplan(null);
    }
  }, [activeGameplanId, activeGameplan, setActiveGameplan]);

  // Shared responsive layout: outer aside owns the open/close
  // dimension animation; the inner surface holds the actual panel
  // content. GameplanPanel is always "open" while mounted (it only
  // exists during the gameplan tab), so we pass open=true so the
  // aside starts at its full open dimension and slideInOnMount
  // gives it the entrance animation.
  const { asideStyle, innerSurfaceStyle, bottomSheet } =
    useResponsivePanelLayout({
      open: true,
      drawerWidth,
      slideInOnMount: true,
    });
  return (
    <>
      <aside ref={panelRef} style={{ ...asideStyle, color: '#e6e8ef' }}>
        <div
          ref={innerRef}
          style={{
            ...innerSurfaceStyle,
            background: '#0e1018',
            display: 'flex',
            flexDirection: 'column',
            ...(bottomSheet
              ? { borderTop: '1px solid rgba(255,255,255,0.08)' }
              : { borderLeft: '1px solid rgba(255,255,255,0.08)' }),
          }}
        >
          {/* Header — shared ViewSwitcher only. The "gameplans · N"
              label was dropped to give the canvas more vertical room;
              the index body already reports the count inline. */}
          <div
            style={{
              padding: '10px 16px 10px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <ViewSwitcher
              activeView="plan"
              hasSelection={isSelectionValid(
                selection,
                positions,
                moves,
                counters,
              )}
            />
          </div>

          {/* Body — route between index and editor. */}
          {activeGameplan === null ? (
            <GameplanIndex
              positions={positions}
              moves={moves}
              counters={counters}
              mediaRefs={mediaRefs}
            />
          ) : (
            <GameplanEditor
              gameplan={activeGameplan}
              positions={positions}
              moves={moves}
              counters={counters}
              mediaRefs={mediaRefs}
            />
          )}
        </div>
      </aside>
      {/* ResizeHandle is mode-aware now — see ResizeHandle.tsx for the
          horizontal-vs-vertical orientation switch. Always rendered. */}
      <ResizeHandle
        open
        applyLiveWidth={(size, axis = 'width') => {
          const cssV = `${size}px`;
          if (panelRef.current) {
            // Horizontal mode mutates width (side drawer); vertical
            // mode (bottom sheet) mutates height. Flexbox parent
            // sees it immediately and the canvas reflows.
            panelRef.current.style[axis] = cssV;
          }
          if (innerRef.current && axis === 'width') {
            // Inner has its own explicit width in side mode (see
            // innerSurfaceStyle in useResponsivePanelLayout). Push
            // the live drag value through so the content tracks
            // the boundary instead of lagging until the drag
            // release commits to the store and React re-renders.
            innerRef.current.style.width = cssV;
          }
        }}
      />
    </>
  );
}
